// IM-EXT-002 IM-UI-030 — Inteligência de Mercado: lista de análises (filtros, arquivadas, paginação, arquivar/reabrir).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { MarketAnalysisSummaryDto } from '@intelimove/contracts';
import { useAuth } from '../auth/AuthContext';
import { AsyncView, EmptyState } from '../components/States';
import { useToast } from '../components/Toast';
import { FilterBar, PageHead, StatusPill } from '../components/Widgets';
import { fmtDate, marketApi, STATUS_LABEL, STATUS_TONE, TYPE_LABEL } from '../data/market';
import { useResource } from '../hooks/useResource';

export function MarketList() {
  const toast = useToast();
  const { me } = useAuth();
  const canManage = me?.activeOrganization?.role !== 'MEMBER';
  const [archived, setArchived] = useState<'exclude' | 'only' | 'include'>('exclude');
  const [status, setStatus] = useState<string>('');
  const [q, setQ] = useState('');
  const [extra, setExtra] = useState<MarketAnalysisSummaryDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [changed, setChanged] = useState<Record<string, MarketAnalysisSummaryDto | null>>({});
  const [confirming, setConfirming] = useState<MarketAnalysisSummaryDto | null>(null);
  const opts = useResource((s) => marketApi.options(s), []);
  const { state, reload } = useResource(
    (s) => marketApi.list({ archived, status: status || null, q }, s).then((p) => { setExtra([]); setChanged({}); setCursor(p.nextCursor); return p; }),
    [archived, status, q],
  );

  const more = async () => {
    if (!cursor) return;
    try {
      const p = await marketApi.list({ archived, status: status || null, q, cursor });
      setExtra((e) => [...e, ...p.items]);
      setCursor(p.nextCursor);
    } catch (e) { toast((e as Error).message, 'error'); }
  };
  const archive = async (a: MarketAnalysisSummaryDto) => {
    try {
      const d = await marketApi.archive(a.id);
      setChanged((m) => ({ ...m, [a.id]: archived === 'exclude' ? null : d }));
      setConfirming(null);
      toast(`Análise “${a.name}” arquivada. Use o filtro “Arquivadas” para consultar ou reabrir.`);
    } catch (e) { toast((e as Error).message, 'error'); }
  };
  const reopen = async (a: MarketAnalysisSummaryDto) => {
    try {
      const d = await marketApi.reopen(a.id);
      setChanged((m) => ({ ...m, [a.id]: archived === 'only' ? null : d }));
      toast(`Análise “${a.name}” reaberta.`);
    } catch (e) { toast((e as Error).message, 'error'); }
  };
  const ready = opts.state.kind === 'ready' && opts.state.data.ready;

  return (
    <section aria-labelledby="page-title">
      <PageHead eyebrow="Inteligência de Mercado" title="Concorrentes e mercado" subtitle="Pesquisas sobre quem disputa seus clientes, o que oferecem e onde estão as oportunidades."
        actions={canManage ? <Link className="cta" to="/mercado/nova" style={{ textDecoration: 'none' }}>Nova análise</Link> : undefined} />
      {opts.state.kind === 'ready' && !ready && (
        <div className="card notice" role="status">
          <b>Confirme o contexto da empresa para pesquisar.</b>
          <p className="card-sub">A pesquisa parte do contexto confirmado (ofertas, ICPs, personas e mercados), para você não precisar explicar de novo quem é a empresa.</p>
          <Link className="small-action" to="/configuracoes/contexto">Completar o contexto</Link>
        </div>
      )}
      <FilterBar label="Filtrar por arquivamento" value={archived} onChange={(v) => setArchived(v)} options={[
        { value: 'exclude', label: 'Ativas' }, { value: 'only', label: 'Arquivadas' }, { value: 'include', label: 'Todas' },
      ]} />
      <div className="market-toolbar">
        <FilterBar label="Filtrar por situação" value={status} onChange={setStatus} options={[
          { value: '', label: 'Qualquer situação' }, { value: 'DRAFT', label: 'Rascunho' }, { value: 'RUNNING', label: 'Pesquisando' },
          { value: 'COMPLETED', label: 'Concluídas' }, { value: 'NEEDS_REVIEW', label: 'Parciais' }, { value: 'FAILED', label: 'Não concluídas' },
        ]} />
        <label className="field market-search"><span className="sr-only">Buscar análise</span>
          <input type="search" placeholder="Buscar por nome ou objetivo" value={q} onChange={(e) => setQ(e.target.value)} /></label>
      </div>
      <AsyncView state={state} onRetry={reload}
        empty={<EmptyState title="Nenhuma análise por aqui">Crie uma análise para descobrir concorrentes, comparar ofertas e receber recomendações comerciais.</EmptyState>}>
        {(page) => {
          const items = [...page.items, ...extra].filter((x) => changed[x.id] !== null).map((x) => changed[x.id] ?? x);
          if (!items.length) return <EmptyState title={archived === 'only' ? 'Nenhuma análise arquivada' : 'Nenhuma análise para este filtro'}>{archived === 'only' ? 'Análises arquivadas aparecem aqui e podem ser reabertas a qualquer momento.' : 'Ajuste os filtros ou crie uma nova análise.'}</EmptyState>;
          return (
            <>
              <p className="card-sub" style={{ margin: '0 0 12px' }}>{page.counts.active} ativa(s) · {page.counts.archived} arquivada(s)</p>
              <div className="market-grid">
                {items.map((a) => (
                  <article key={a.id} className="card market-card">
                    <div className="signal-card-top">
                      <span className="tag mint">{TYPE_LABEL[a.analysisType] ?? a.analysisType}</span>
                      <StatusPill tone={STATUS_TONE[a.visibleState] ?? 'default'}>{STATUS_LABEL[a.visibleState] ?? a.visibleState}</StatusPill>
                    </div>
                    <h3><Link to={`/mercado/${a.id}`}>{a.name}</Link></h3>
                    <p className="card-sub">{a.objective}</p>
                    <dl className="kv small">
                      <dt>Ofertas</dt><dd>{a.offerings.join(', ') || '—'}</dd>
                      <dt>Região</dt><dd>{a.region ?? 'Sugerida pelo contexto'}</dd>
                      <dt>Contexto</dt><dd>versão {a.contextVersion}</dd>
                      <dt>Criada</dt><dd>{fmtDate(a.createdAt)}</dd>
                      <dt>Última execução</dt><dd>{fmtDate(a.lastRunAt)}{a.runsCount > 1 ? ` · ${a.runsCount} execuções` : ''}</dd>
                    </dl>
                    {a.summary && <p className="market-summary">{a.summary}</p>}
                    {a.competitorsFound > 0 && <p className="cell-sub">{a.competitorsFound} concorrente(s) encontrado(s) na última execução</p>}
                    <div className="row-actions">
                      <Link className="small-action" to={`/mercado/${a.id}`}>Abrir</Link>
                      <Link className="small-action" to={`/mercado/${a.id}?aba=historico`}>Histórico</Link>
                      {canManage && (a.archived
                        ? <button type="button" className="small-action" onClick={() => reopen(a)}>Reabrir</button>
                        : <button type="button" className="small-action" onClick={() => setConfirming(a)} disabled={['QUEUED', 'RUNNING'].includes(a.status)}>Arquivar</button>)}
                    </div>
                  </article>
                ))}
              </div>
              {cursor && <div className="load-more"><button className="secondary" onClick={more}>Carregar mais análises</button></div>}
            </>
          );
        }}
      </AsyncView>
      {confirming && <ArchiveDialog name={confirming.name} onCancel={() => setConfirming(null)} onConfirm={() => archive(confirming)} />}
    </section>
  );
}

/** Confirmação antes de arquivar: diz o que acontece e como desfazer. Nada é excluído. */
export function ArchiveDialog({ name, onCancel, onConfirm }: { name: string; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="dialog-backdrop" role="presentation" onClick={onCancel}>
      <div className="dialog card" role="alertdialog" aria-modal="true" aria-labelledby="archive-title" onClick={(e) => e.stopPropagation()}>
        <h2 id="archive-title">Arquivar “{name}”?</h2>
        <p className="card-sub">A análise sai da lista de ativas, mas nada é excluído: resultados, evidências e histórico continuam disponíveis no filtro “Arquivadas”, e você pode reabri-la quando quiser.</p>
        <div className="form-actions">
          <button type="button" className="secondary" onClick={onCancel} autoFocus>Cancelar</button>
          <button type="button" className="cta" onClick={onConfirm}>Arquivar análise</button>
        </div>
      </div>
    </div>
  );
}
